import { CssBaseline } from '@mui/material';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getTheme, AppTheme, ColorMode } from '../../theme';

/**
 * Color mode context value interface
 */
type ColorModeContextValue = {
  readonly mode: ColorMode;
  readonly toggleColorMode: () => void;
  readonly setColorMode: (mode: ColorMode) => void;
  readonly theme: AppTheme;
  readonly prefersReducedMotion: boolean;
};

/**
 * Context for color mode management
 */
const ColorModeContext = createContext<ColorModeContextValue | null>(null);

/**
 * Local storage key for color mode preference
 */
const COLOR_MODE_STORAGE_KEY = 'penticton-dev-meetup-color-mode';

/**
 * Gets the initial color mode based on:
 * 1. Stored preference in localStorage
 * 2. System preference (prefers-color-scheme)
 * 3. Default to light mode
 */
const getInitialColorMode = (): ColorMode => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return 'light';
  }

  try {
    // First, check localStorage for saved preference
    const savedMode = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    if (savedMode && (savedMode === 'light' || savedMode === 'dark')) {
      return savedMode as ColorMode;
    }

    // If no saved preference, check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
  } catch (error) {
    // If localStorage is not available or throws an error, fall back to light mode
    console.warn('Unable to access localStorage for color mode preference:', error);
  }

  // Default to light mode
  return 'light';
};

/**
 * Saves the color mode preference to localStorage
 */
const saveColorModePreference = (mode: ColorMode): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
  } catch (error) {
    console.warn('Unable to save color mode preference to localStorage:', error);
  }
};

/**
 * Theme provider component that manages color mode state and persistence
 */
const ThemeProvider = ({ children }: { readonly children: ReactNode }) => {
  const [mode, setModeState] = useState<ColorMode>(getInitialColorMode);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState<boolean>(false);

  // Update the mode and persist to localStorage
  const setColorMode = useCallback((newMode: ColorMode) => {
    setModeState(newMode);
    saveColorModePreference(newMode);
  }, []);

  // Toggle between light and dark modes
  const toggleColorMode = useCallback(() => {
    const newMode: ColorMode = mode === 'light' ? 'dark' : 'light';
    setColorMode(newMode);
  }, [mode, setColorMode]);

  // Listen for system color scheme and motion preference changes
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // Color scheme preference
    const colorSchemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleColorSchemeChange = (event: MediaQueryListEvent) => {
      // Only update if user hasn't explicitly set a preference
      try {
        const savedMode = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
        if (!savedMode) {
          setModeState(event.matches ? 'dark' : 'light');
        }
      } catch {
        // If localStorage is not available, update based on system preference
        setModeState(event.matches ? 'dark' : 'light');
      }
    };

    // Set initial color scheme if not already set
    try {
      const savedMode = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
      if (!savedMode && colorSchemeQuery?.matches) {
        setModeState('dark');
      }
    } catch {
      // If localStorage is not available, use system preference
      if (colorSchemeQuery?.matches) {
        setModeState('dark');
      }
    }

    // Motion preference
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleMotionPreferenceChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    // Set initial motion preference
    setPrefersReducedMotion(motionQuery?.matches ?? false);

    // Add listeners
    colorSchemeQuery?.addEventListener('change', handleColorSchemeChange);
    motionQuery?.addEventListener('change', handleMotionPreferenceChange);

    // Cleanup listeners on unmount
    return () => {
      colorSchemeQuery?.removeEventListener('change', handleColorSchemeChange);
      motionQuery?.removeEventListener('change', handleMotionPreferenceChange);
    };
  }, []);

  // Generate theme based on current mode and motion preference
  const theme = useMemo(
    () => getTheme(mode, prefersReducedMotion),
    [mode, prefersReducedMotion],
  );

  // Context value
  const value = useMemo<ColorModeContextValue>(
    () => ({ mode, toggleColorMode, setColorMode, theme, prefersReducedMotion }),
    [mode, toggleColorMode, setColorMode, theme, prefersReducedMotion],
  );

  return (
    <ColorModeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </MuiThemeProvider>
    </ColorModeContext.Provider>
  );
};

/**
 * Hook to access color mode context
 */
export const useColorMode = (): ColorModeContextValue => {
  const context = useContext(ColorModeContext);

  if (!context) {
    throw new Error('useColorMode must be used within ThemeProvider');
  }

  return context;
};

/**
 * Hook to access the current theme (shorthand)
 */
export const useAppTheme = (): AppTheme => {
  const { theme } = useColorMode();
  return theme;
};

export default ThemeProvider;
